import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ReactComponent as FilterClose } from '../../../assets/icons/filter-close.svg';
import DefaultApiLimit from '../../../config/defaultLimit';
import { PricingList } from '../../../interface/dynamic-pricing.interface';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import {
  fetchAllDynamicPrice,
  setDynamicFilterData,
  removeFilters,
  setSortData,
} from '../../../redux/slice/DynamicBasePriceManagementSlice';
import { setNotificationDetails } from '../../../redux/slice/NotificationSlice';
import { RootState } from '../../../redux/store';
import MyButton from '../../../shared-components/button/Button';
import CustomDateRanges from '../../../shared-components/custom-date-range/CustomDateRanges';
import CustomTable from '../../../shared-components/custom-table/CustomTable';
import { exportCSV, exportPDF } from '../../../shared-functions/ExportFile';
import ToFixedValue from '../../../shared-functions/ToFixedValue';
import dynamicPrizeApi from '../../../utils/api/dynamic-prize';

/**
 * value
 * @returns
 */
function BaseRateList(properties: { stationId: string }) {
  const { stationId } = properties;
  const dispatch = useAppDispatch();
  const { t } = useTranslation('dynamicPricing');
  const [exportLoader, setExportLoader] = useState<boolean>(false);
  const [hiddenColumns, setHiddenColumns] = useState<string[]>([]);
  const {
    pricing,
    page,
    limit,
    totalPages,
    totalResults,
    loading,
    filter,
    isFilterUpdated,
    sortBy,
    sortField,
  } = useAppSelector((states: RootState) => states.dynamicBasePrice);

  const reportsColumn = [
    {
      label: t('stationName'),
      accessorKey: 'chargingStationName' as keyof PricingList,
      sortAllow: true,
    },
    {
      label: t('date'),
      accessorKey: 'createdAt' as keyof PricingList,
      sortAllow: true,
    },
    {
      label: t('baseRate'),
      accessorKey: 'price' as keyof PricingList,
      sortAllow: true,
    },
    {
      label: t('revenue'),
      accessorKey: 'totalRevenue' as keyof PricingList,
      sortAllow: true,
    },
  ];

  /**
   * Handle the fetch a pricing data use api function
   * @param data
   */
  const fetchData = (data: {
    page: number;
    limit: number;
    pricingType: string;
    sortBy?: string;
    sortField?: string;
  }) => {
    if (!loading && stationId) {
      dispatch(fetchAllDynamicPrice({ ...data, stationId, ...filter }));
    }
  };

  /**
   * Handle the value change function
   * @param name
   * @param changeValue
   */
  const onChangeValue = (name: string, changeValue: string | number) => {
    dispatch(
      setDynamicFilterData({
        ...filter,
        [name]: changeValue,
      }),
    );
  };

  /**
   * Handles the change of the current page number.
   * @param {number} pageNumber - The new page number.
   * @returns {void}
   */
  const handlePageChange = (pageNumber: number) => {
    fetchData({
      page: pageNumber,
      limit,
      pricingType: 'base',
      sortBy,
      sortField,
    });
  };
  /**
   * Handles the change of the items limit per page.
   * @param {number} changeLimit - The new limit of items per page.
   * @returns {void}
   */
  const handleLimitChange = (changeLimit: number) => {
    fetchData({
      page: DefaultApiLimit.page,
      limit: changeLimit,
      pricingType: 'base',
      sortBy,
      sortField,
    });
  };

  /**
   * Handle the filter data clear function
   */
  const handleSortFunction = (sortData: { sortBy: 'asc' | 'desc'; sortField: string }) => {
    dispatch(setSortData(sortData));
    fetchData({
      ...filter,
      page: DefaultApiLimit.page,
      limit,
      pricingType: 'base',
      sortBy: sortData.sortBy,
      sortField: sortData.sortField,
    });
  };

  /**
   * Current columns for export
   */
  const currentColumns = [
    { label: t('sNo'), accessorKey: '__serial__' },
    { label: t('stationName'), accessorKey: 'chargingStationName' },
    { label: t('date'), accessorKey: 'createdAt' },
    { label: t('baseRate'), accessorKey: 'price' },
    { label: t('revenue'), accessorKey: 'totalRevenue' },
  ];

  /**
   * Filtering columns for export without hidden columns
   */
  const exportCurrentColumns = currentColumns.filter(
    (col) => col.accessorKey === '__serial__' || !hiddenColumns.includes(col.accessorKey),
  );

  /**
   * Export data function
   */
  const exportDataPdfOrCSV = (type: string) => {
    setExportLoader(true);
    dynamicPrizeApi
      .fetchDynamicPrizeExport({ ...filter, pricingType: 'base', sortBy, sortField })
      .then((response) => {
        // Convert data to CSV format
        const headers = exportCurrentColumns.map((col) => col.label);
        const rows = response.data.results.map((row: PricingList, index: number) =>
          exportCurrentColumns.map((col) => {
            switch (col.accessorKey) {
              case '__serial__':
                return index + 1;
              case 'price':
                return ToFixedValue(row?.price) || 'N/A';
              case 'totalRevenue':
                return ToFixedValue(row?.totalRevenue) || 'N/A';
              default:
                return (row as any)[col?.accessorKey] || 'N/A';
            }
          }),
        );
        setExportLoader(false);
        if (rows.length > 0) {
          if (type === 'csv') {
            exportCSV(headers, rows, 'base-price-list.csv');
          } else {
            exportPDF(headers, rows, 'base-price-list.pdf');
          }
        }
      })
      .catch((error) => {
        setExportLoader(false);
        dispatch(
          setNotificationDetails({
            type: 'error',
            notificationDetails: {
              statusCode: error?.response?.data?.code || 404,
              message: error?.response?.data?.message || t('somethingWentWrong'),
            },
          }),
        );
      });
  };

  /**
   * function to clear selected date filter
   */
  const filterClearFunction = () => {
    dispatch(removeFilters());
    dispatch(setSortData({ sortBy: '', sortField: '' }));

    fetchData({
      page: DefaultApiLimit.page,
      limit: DefaultApiLimit.limit,
      pricingType: 'base',
      sortBy: '',
      sortField: '',
    });
  };

  /**
   * Function to refresh data
   */
  const handleRefresh = () => {
    fetchData({ page, limit, pricingType: 'base', sortBy, sortField });
  };

  useEffect(() => {
    if (isFilterUpdated || pricing.length === 0) {
      fetchData({
        ...filter,
        page: DefaultApiLimit.page,
        limit: DefaultApiLimit.limit,
        pricingType: 'base',
      });
    }
  }, [filter]);

  useEffect(() => {
    fetchData({
      ...filter,
      page: DefaultApiLimit.page,
      limit: DefaultApiLimit.limit,
      pricingType: 'base',
    });
  }, [stationId]);

  return (
    <CustomTable
      header={reportsColumn}
      data={pricing}
      paginate
      loading={loading}
      limit={limit}
      currentPage={page}
      totalPages={totalPages}
      totalResults={totalResults}
      sortData={{ sortBy, sortField }}
      handleSortChange={handleSortFunction}
      handlePageChange={handlePageChange}
      handleLimitChange={handleLimitChange}
      filterAllow={false}
      tableHeight="70vh"
      leftTopContent={
        <div className="reports-custom-date">
          <div>
            <MyButton
              label=""
              buttonType="none"
              onClickFunc={filterClearFunction}
              svgBackIcon={<FilterClose />}
              hoverLabel={t('clearFilter')}
            />{' '}
          </div>
          <CustomDateRanges
            inputLabel=""
            name1="startDate"
            value1={filter.startDate || ''}
            name2="endDate"
            value2={filter.endDate || ''}
            onChangeValue={onChangeValue}
          />  
        </div>
      }
      exportData={pricing}
      exportDataPdfOrCSV={exportDataPdfOrCSV}
      exportLoader={exportLoader}
      refreshRequired
      handleRefresh={handleRefresh}
      hoverLabel={t('refresh')}
      serialNo
      oldVersion
      onHiddenColumnsChange={setHiddenColumns}
    />
  );
}

export default BaseRateList;
